import { useEffect, useState } from "react";

import Chart, { type AxisGranularity, type SupportedChartType } from "./Chart";
import { asKpiFormat, inferFormatFromName } from "../lib/format";
import { seriesColors } from "../lib/chartPalette.mjs";
import { resolveReferenceLines } from "../lib/referenceLines.mjs";
import { widgetDomain } from "../lib/widgetDomain.mjs";
import { resolveFeedGap } from "../lib/missingFeed.mjs";
import EmptyState from "./states/EmptyState";
import ErrorState from "./states/ErrorState";
import Skeleton from "./states/Skeleton";

import { obfuscatePII, useDemoMode } from "../lib/demoMode";
import { humanize } from "../lib/labels";
import { type ChartSeries, type ManifestWidget } from "../lib/manifest";
import {
  GATE_COPY,
  PCT_DISPLAY_LIMIT,
  isColumnMissingError,
  isUnboundedPctColumn,
} from "../lib/sanityGate";
import { evaluateAst, parseMeasureExpression, uniqueTerms } from "../lib/measureExpression";
import {
  DERIVED_RATIOS,
  applyFeedQuality,
  applyFilters,
  resolveLatestFilters,
  applyNowCutoff,
  applyTimeline,
  canonicalColumnName,
  resolveSourceName,
} from "../lib/queries";
import { divisionColumnFor } from "../lib/divisionSources.mjs";
import { getSupabase } from "../lib/supabase";
import { useTenantId } from "../lib/tenant";
import { scopeTenantTable } from "../lib/tenantScope.mjs";

/**
 * Chart widget renderer for `type: chart` manifest entries that declare
 * `x_axis` + `y_axis_series`. Each series fires a grouped PostgREST
 * aggregate (`?select={x_axis},{col}.{agg}()`) and the results assemble
 * into Chart.js {labels, datasets}.
 *
 * Manifest pattern (e.g. L1.3 Revenue Trend on the DWS Revenue tab):
 *   x_axis: period_date
 *   y_axis_series:
 *     - { column: total_revenue, aggregation: sum, label: Earned, color: blue }
 *   chart_subtype: stacked_bar
 *
 * Each ChartSeries becomes a Chart.js dataset. For stacked_bar widgets,
 * Chart.js stacks the datasets via its scale options (handled in Chart.tsx).
 */

export interface ChartWidgetProps {
  widget: ManifestWidget;
  xAxis: string;
  series: ChartSeries[];
  chartType: SupportedChartType;
}

interface SeriesPoints {
  pointsByLabel: Map<string, number | null>;
  error: string | null;
}

// MER-286: expression parser lives in lib/measureExpression.ts. Supports
// SIMPLE `agg(col) op agg(col)` plus COMPOUND `(agg(col) op agg(col)) op
// agg(col)` for weighted-ratio series.

// MER-153: brand palette per wrapper mockup. brand-teal is the default
// chart color (replaces the prior Chart.js blue). `blue` aliases to the
// brand-teal so legacy manifest widgets declaring `color: blue` render
// in brand without a manifest edit.
const NAMED_COLORS: Record<string, { bg: string; border: string }> = {
  brand: { bg: "rgba(38, 168, 159, 0.75)", border: "rgba(15, 107, 92, 1)" },
  teal: { bg: "rgba(38, 168, 159, 0.75)", border: "rgba(15, 107, 92, 1)" },
  blue: { bg: "rgba(38, 168, 159, 0.75)", border: "rgba(15, 107, 92, 1)" },
  green: { bg: "rgba(15, 107, 92, 0.75)", border: "rgba(15, 107, 92, 1)" },
  mint: { bg: "rgba(168, 224, 212, 0.85)", border: "rgba(38, 168, 159, 1)" },
  red: { bg: "rgba(192, 57, 43, 0.7)", border: "rgba(192, 57, 43, 1)" },
  amber: { bg: "rgba(230, 126, 34, 0.7)", border: "rgba(230, 126, 34, 1)" },
  gray: { bg: "rgba(140, 140, 140, 0.6)", border: "rgba(140, 140, 140, 1)" },
};

function paletteFor(name: string | undefined, index: number) {
  if (name && NAMED_COLORS[name]) return NAMED_COLORS[name];
  const order = ["brand", "green", "mint", "amber", "red", "gray"];
  return NAMED_COLORS[order[index % order.length]];
}

/**
 * MER-581: series colours for a chart, in its own domain's hue.
 *
 * Replaces the NAMED_COLORS lookup for every chart. That map had `brand`,
 * `teal` and `blue` as BYTE-IDENTICAL entries, so three different declared
 * colours painted the same teal and a Revenue chart was indistinguishable from
 * a Disposal one. A manifest `color:` is now only honoured when it names a
 * status meaning (red / amber) that the domain hue must not override.
 */
function domainSeriesColors(widget: ManifestWidget, count: number) {
  return seriesColors(widgetDomain(widget), count);
}

/** Manifest colours that carry STATUS meaning and outrank the domain hue. */
const STATUS_COLORS = new Set(["red", "amber"]);

/**
 * MER-152 part 3: walk min..max of a sorted ISO-date label list and
 * insert the canonical bucket strings for any periods missing from the
 * source data. The chart then renders those positions with no bar
 * (Chart.js treats null as a gap) but the x-axis still shows the
 * tick, so a missing month is visible instead of silently squashed.
 *
 * Detects granularity by label format:
 *   - YYYY-MM-01 (monthly): step by 1 month
 *   - YYYY-MM-DD where day != 01 (weekly): step by 7 days
 *   - YYYY-MM-DD (daily): step by 1 day
 *
 * Non-date labels pass through unchanged (e.g. equipment_type axis,
 * customer_id axis, etc).
 */
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function _addDays(iso: string, days: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function _addMonths(iso: string, months: number): string {
  const d = new Date(iso + "T00:00:00Z");
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

/**
 * MER-161: classify sorted ISO-date labels as monthly / weekly /
 * daily for downstream x-axis tick formatting. Returns "category"
 * when labels aren't ISO dates so the chart skips date reformatting.
 */
export function sniffGranularity(sorted: string[]): AxisGranularity {
  if (sorted.length === 0 || !sorted.every((l) => ISO_DATE_RE.test(l))) return "category";
  if (sorted.every((l) => l.slice(-2) === "01")) return "monthly";
  if (sorted.length < 2) return "daily";
  const dow = new Date(sorted[0] + "T00:00:00Z").getUTCDay();
  const allSameDow = sorted.every((l) => new Date(l + "T00:00:00Z").getUTCDay() === dow);
  const anyWeekGap = sorted.slice(1).some((l, i) => {
    const prev = new Date(sorted[i] + "T00:00:00Z").getTime();
    const cur = new Date(l + "T00:00:00Z").getTime();
    return Math.round((cur - prev) / 86_400_000) === 7;
  });
  return allSameDow && anyWeekGap ? "weekly" : "daily";
}

export function fillTemporalGaps(sorted: string[]): string[] {
  if (sorted.length < 2) return sorted;
  if (!sorted.every((l) => ISO_DATE_RE.test(l))) return sorted;

  const granularity = sniffGranularity(sorted);
  const isMonthly = granularity === "monthly";
  const isWeekly = granularity === "weekly";

  const step = (cursor: string): string =>
    isMonthly ? _addMonths(cursor, 1) : isWeekly ? _addDays(cursor, 7) : _addDays(cursor, 1);

  const filled: string[] = [sorted[0]];
  for (let i = 1; i < sorted.length; i++) {
    let cursor = step(filled[filled.length - 1]);
    // Walk forward inserting missing buckets up to (but not past) the
    // next real label. Cap at 500 inserts as a safety against
    // mis-detected granularity producing runaway loops.
    let safety = 0;
    while (cursor < sorted[i] && safety < 500) {
      filled.push(cursor);
      cursor = step(cursor);
      safety++;
    }
    filled.push(sorted[i]);
  }
  return filled;
}

async function runGroupedAggregate(
  table: string,
  xAxis: string,
  column: string,
  agg: string,
  widget: ManifestWidget,
  tenantId: string,
): Promise<Map<string, number | null>> {
  // MER-589: resolve any {op:"latest"} pin before the series query builds.
  widget = await resolveLatestFilters(widget, tenantId);
  const supabase = getSupabase();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let q: any = scopeTenantTable(
    (supabase.from(table) as any).select(`${xAxis},${column}.${agg}()`),
    tenantId,
    table,
  );
  q = applyFilters(q, widget);
  q = applyTimeline(q, widget);
  q = applyNowCutoff(q, widget, table);
  // MER-693: this is the per-bucket twin of the KPI-tile aggregate, so it has
  // to make the same call about a starved period. Without it an `avg` tile on
  // kpi_summary and the trend chart of the same metric beside it print
  // different numbers for the same window, with nothing on either face to
  // explain the difference. applyFeedQuality is a no-op for sums and counts
  // and for every table that does not carry data_quality.
  q = applyFeedQuality(q, table, agg);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const out = new Map<string, number | null>();
  if (!Array.isArray(data)) return out;
  for (const row of data as Record<string, unknown>[]) {
    const key = String(row[xAxis] ?? "");
    const v = row[agg] ?? row[column];
    if (v === null || v === undefined) {
      out.set(key, null);
      continue;
    }
    const n = Number(v);
    out.set(key, Number.isNaN(n) ? null : n);
  }
  return out;
}

/**
 * MER-640: the widget filter with this series' own clauses laid over it.
 *
 * Series used to share the widget object verbatim, so every series of a chart
 * asked PostgREST the same question: RO Container Idle Time declared five
 * `count(event_id)` buckets labelled "Idle <7d" through ">60d" and drew August
 * roll-off five times, stacking 1,582 to 7,910. A bucket is only a bucket if
 * something narrows it. Same overlay shape executeKpiQuery already uses for
 * net_count / ratio_count sides, so the clause vocabulary is identical and
 * `resolveLatestFilters` (called inside runGroupedAggregate) still resolves an
 * `{op: latest}` on either level.
 */
function seriesWidget(widget: ManifestWidget, s: ChartSeries): ManifestWidget {
  if (!s.filter) return widget;
  return { ...widget, filter: { ...(widget.filter ?? {}), ...s.filter } } as ManifestWidget;
}

async function resolveSeries(
  s: ChartSeries,
  xAxis: string,
  table: string,
  widget: ManifestWidget,
  tenantId: string,
): Promise<Map<string, number | null>> {
  // Every runGroupedAggregate below reads this, not the bare widget, so a
  // per-series filter reaches the plain-column path and each term of an
  // expression / derived-ratio alike.
  const scoped = seriesWidget(widget, s);
  // MER-483: derived-ratio columns (arpc etc.) exist only in the
  // DERIVED_RATIOS registry, not as raw table columns; selecting them
  // raw 42703s (the live arpc red box on Exec Roll-Up). Trend them as
  // per-bucket SUM(num) / SUM(denom) through the expression path: same
  // unbiased formula the KPI tile path uses, and applyOp already nulls
  // a zero-denominator bucket so it renders as a gap, not a spike.
  // The series' declared aggregation is intentionally ignored here:
  // avg-of-stored-ratios is the bias MER-320 removed.
  if (s.column) {
    const derived = DERIVED_RATIOS[canonicalColumnName(s.column)];
    if (derived && derived.table === table) {
      return resolveSeries(
        {
          ...s,
          column: undefined,
          aggregation: undefined,
          expression: `sum(${derived.num}) / sum(${derived.denom})`,
        },
        xAxis,
        table,
        widget,
        tenantId,
      );
    }
  }
  if (s.column && s.aggregation) {
    return runGroupedAggregate(
      table,
      xAxis,
      canonicalColumnName(s.column),
      s.aggregation,
      scoped,
      tenantId,
    );
  }
  if (s.expression) {
    const ast = parseMeasureExpression(s.expression);
    if (!ast) return new Map();
    const terms = uniqueTerms(ast);
    const aggregated = await Promise.all(
      terms.map((t) =>
        runGroupedAggregate(table, xAxis, canonicalColumnName(t.column), t.agg, scoped, tenantId),
      ),
    );
    const out = new Map<string, number | null>();
    const keys = new Set<string>();
    aggregated.forEach((m) => m.forEach((_v, k) => keys.add(k)));
    for (const k of keys) {
      const values = aggregated.map((m) => m.get(k));
      out.set(k, evaluateAst(ast, values));
    }
    return out;
  }
  return new Map();
}

/**
 * MER-697: the column a chart's x-axis actually selects.
 *
 * `canonicalColumnName` alone lowercases the authored name and stops there, so
 * AR Aging by Division asked financial_health for `division_name` (the
 * template wrote `Division_Name`) while migration 0002 calls the column
 * `division`. PostgREST 42703'd on every series, `isColumnMissingError`
 * painted the awaiting-source-data gate, and a chart with $448,130 of Jackson
 * current AR behind it rendered blank. The division filter path already asks
 * `divisionColumnFor` which name a given source uses (MER-582); the x-axis
 * path never did. Ask the same question here, so the two can never disagree.
 */
export function chartXAxisColumn(xAxis: string, widget: ManifestWidget): string {
  const canonical = canonicalColumnName(xAxis);
  if (canonical !== "division" && canonical !== "division_name") return canonical;
  if (!widget.source) return canonical;
  return divisionColumnFor(resolveSourceName(widget.source));
}

export default function ChartWidget(props: ChartWidgetProps) {
  const { widget, xAxis, series, chartType } = props;
  // MER-291: obfuscate PII x-axis labels in demo mode (route_id,
  // customer_id, driver_id). Hook must run unconditionally.
  const demoMode = useDemoMode();
  const [state, setState] = useState<{
    seriesPoints: SeriesPoints[];
    labels: string[];
    isLoading: boolean;
  }>({ seriesPoints: [], labels: [], isLoading: true });

  const canonicalXAxis = chartXAxisColumn(xAxis, widget);
  const tenantId = useTenantId();

  useEffect(() => {
    if (!tenantId) {
      setState({ seriesPoints: [], labels: [], isLoading: true });
      return;
    }
    const boundTenantId = tenantId;
    let cancelled = false;
    setState({ seriesPoints: [], labels: [], isLoading: true });

    async function run() {
      if (!widget.source) {
        if (!cancelled)
          setState({
            seriesPoints: series.map(() => ({
              pointsByLabel: new Map(),
              error: "Missing source",
            })),
            labels: [],
            isLoading: false,
          });
        return;
      }
      const table = resolveSourceName(widget.source);
      const settled = await Promise.allSettled(
        series.map((s) => resolveSeries(s, canonicalXAxis, table, widget, boundTenantId)),
      );
      if (cancelled) return;
      const seriesPoints: SeriesPoints[] = settled.map((s) =>
        s.status === "fulfilled"
          ? { pointsByLabel: s.value, error: null }
          : {
              pointsByLabel: new Map(),
              error: s.reason instanceof Error ? s.reason.message : String(s.reason),
            },
      );
      // Union of x-axis labels across series, plus any missing buckets
      // between min and max so gaps in the time series render as a
      // visible x-axis tick with no bar instead of silently collapsing
      // (MER-152 part 3).
      const allLabels = new Set<string>();
      for (const sp of seriesPoints) {
        for (const k of sp.pointsByLabel.keys()) allLabels.add(k);
      }
      const sortedLabels = Array.from(allLabels).sort();
      const labels = fillTemporalGaps(sortedLabels);
      setState({ seriesPoints, labels, isLoading: false });
    }

    void run();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widget, tenantId]);

  if (state.isLoading) return <Skeleton label={widget.name} />;
  // If every series errored, surface the first error.
  const allErrored = state.seriesPoints.length > 0 && state.seriesPoints.every((s) => s.error);
  if (allErrored) {
    const message = state.seriesPoints[0].error ?? "Chart series failed to resolve";
    // MER-483: a phantom column must render the honest awaiting state,
    // never a raw SQL error on a client page (mirrors WidgetRenderer's
    // MER-446 gate, which this per-series fetch path bypasses).
    if (isColumnMissingError(message)) {
      // AJ 2026-09-02: name the feed where we can. Best Disposal's Safety by
      // Division and Harsh Events charts land here on every render because that
      // tenant has no asset rows at all, and "awaiting source data" does not
      // tell their owner which feed to switch on.
      const feedGap = resolveFeedGap({
        columns: series.map((serie) => serie.column),
        source: widget.source ? resolveSourceName(widget.source) : undefined,
        noSourceData: true,
      });
      return <EmptyState title={widget.name} caption={feedGap?.copy ?? GATE_COPY} />;
    }
    return <ErrorState message={message} />;
  }
  if (state.labels.length === 0) return <EmptyState />;

  // MER-161 / MER-438: Y-axis unit. Inferred from the first series when the
  // widget omits display_format.
  const firstSeries = series[0];
  const widgetFormat = widget.display_format
    ? asKpiFormat(widget.display_format)
    : (inferFormatFromName(firstSeries?.column, firstSeries?.label) ?? asKpiFormat(undefined));

  // MER-441: drop "unknown"-bucket labels from customer charts (matches the
  // MER-438 table rule), then drop percent points beyond +/-200% so one broken
  // route (a -3500% CM from a starved denominator) can't blow out the axis.
  const labels = state.labels.filter((l) => String(humanize(l)).toLowerCase() !== "unknown");
  const gatePoint = (v: number | null, applyPctLimit: boolean): number | null =>
    v !== null && applyPctLimit && widgetFormat === "percent" && Math.abs(v) > PCT_DISPLAY_LIMIT
      ? null
      : v;

  // MER-581: one ramp for the whole chart, computed once rather than per
  // series, so every dataset draws from the same domain hue.
  const domainRamp = domainSeriesColors(widget, series.length);
  const datasets = series.map((s, i) => {
    const sp = state.seriesPoints[i];
    const color =
      s.color && STATUS_COLORS.has(s.color)
        ? paletteFor(s.color, i)
        : (domainRamp[i] ?? domainRamp[domainRamp.length - 1]);
    // Per-series: a growth/variance/change series can legitimately exceed +/-200%,
    // so exempt it from the point-drop. Bounded ratios (margin, rate) still gate.
    const applyPctLimit = !isUnboundedPctColumn(s.column);
    // MER-462: pie colors are per-SLICE, not per-dataset. A single bg
    // color renders every slice identical; cycle the palette per label.
    const sliceColors =
      chartType === "pie"
        ? // MER-581: pie slices are the one place a chart genuinely carries
          // several identities, so the ramp spans the slice count. Slice count
          // is data-driven (payment methods can grow), so the ramp is computed
          // from labels.length rather than a fixed cycle that silently repeated
          // a colour on the 7th slice.
          (() => {
            const ramp = domainSeriesColors(widget, Math.max(1, labels.length));
            return labels.map((_, li) => ramp[li] ?? ramp[ramp.length - 1]);
          })()
        : null;
    return {
      label: s.label,
      data: labels.map((l) =>
        gatePoint((sp.pointsByLabel.get(l) ?? null) as number | null, applyPctLimit),
      ),
      backgroundColor: sliceColors ? sliceColors.map((c) => c.bg) : color.bg,
      borderColor: sliceColors ? sliceColors.map((c) => c.border) : color.border,
      borderWidth: 1,
    };
  });

  // MER-441: if nothing real survives -- no labels left, or every point is null
  // (the "40 raw ISO timestamps with empty bars" case) -- render the Pending
  // card, never bare axes or raw-timestamp labels.
  const hasData = labels.length > 0 && datasets.some((d) => d.data.some((v) => v !== null));
  if (!hasData) return <EmptyState />;

  const granularity: AxisGranularity = sniffGranularity(labels);

  // MER-291: obfuscate PII labels (route_id / customer_id / driver_id)
  // when demo mode is on. Granularity / dataset mapping stay keyed on
  // the original labels (which match the dataset point map keys); only
  // the displayed tick labels swap.
  const displayLabels = labels.map((l) => obfuscatePII(l, xAxis, demoMode) as string);

  // MER-605: manifest `reference_lines` (Heath's break-even at 100 on
  // 1.4.2, the 30% margin floor on L4.2b) had zero readers in src, so the
  // field was dead config and no line ever drew. Resolve against the same
  // widgetFormat the y-axis ticks use, so the line lands on the gridline
  // the axis labels with that number.
  const referenceLines = resolveReferenceLines(widget.reference_lines, widgetFormat);

  return (
    <Chart
      type={chartType}
      data={{
        labels: displayLabels,
        datasets,
      }}
      format={widgetFormat}
      xAxisGranularity={granularity}
      name={widget.name}
      referenceLines={referenceLines}
    />
  );
}
